import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CodeIcon from '@mui/icons-material/Code';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import ForestOutlinedIcon from '@mui/icons-material/ForestOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';

import { useAuth } from '../contexts/AuthContext';
import { useRoles } from '../api/admin';
import { useRealtimeKindsSetting } from '../api/realtimeKinds';
import AdminOverview from '../components/admin/AdminOverview';
import UsersManagement from '../components/admin/UsersManagement';
import RolesManagement from '../components/admin/RolesManagement';
import SSOManagement from '../components/admin/SSOManagement';
import BrandingManagement from '../components/admin/BrandingManagement';
import TenantsManagement from '../components/admin/TenantsManagement';
import APIClientsManagement from '../components/admin/APIClientsManagement';
import GisServicesManagement from '../components/admin/GisServicesManagement';
import MCPConnectionsManagement from '../components/admin/MCPConnectionsManagement';
import AgentInstructionsManagement from '../components/admin/AgentInstructionsManagement';
import TenantCapabilitiesManagement from '../components/admin/TenantCapabilitiesManagement';
import { ADMIN_PAGES, ADMIN_SETTINGS_INDEX, AdminPageKey, type SettingsIndexEntry } from '../components/admin/adminNavigation';

/**
 * The admin area: a left navigation grouped by purpose, one page per subject,
 * an Overview landing page and a search box over every setting.
 *
 * Routes: /admin (Overview), /admin/<page>, /admin/people/:userId (People with
 * the person drawer open). Which pages appear depends on the server's own
 * answers: `can_edit` on the realtime-kinds setting says "FiSci staff", and
 * `can_create_tenants` on /api/me says "may run client organisations".
 */

const ICONS: Record<AdminPageKey, React.ReactNode> = {
  overview: <HomeOutlinedIcon fontSize="small" />,
  people: <PeopleOutlineIcon fontSize="small" />,
  roles: <VpnKeyOutlinedIcon fontSize="small" />,
  'sign-in': <LockOutlinedIcon fontSize="small" />,
  organisation: <BusinessOutlinedIcon fontSize="small" />,
  organisations: <AccountTreeOutlinedIcon fontSize="small" />,
  'api-keys': <CodeIcon fontSize="small" />,
  gis: <MapOutlinedIcon fontSize="small" />,
  'ai-assistants': <SmartToyOutlinedIcon fontSize="small" />,
  'standing-orders': <AssignmentOutlinedIcon fontSize="small" />,
  entitlements: <StarOutlineIcon fontSize="small" />,
};

const EXTERNAL_LINKS = [
  { label: 'Data sources', to: '/data-management', icon: <StorageOutlinedIcon fontSize="small" /> },
  { label: 'Vegetation types', to: '/vegetation-management', icon: <ForestOutlinedIcon fontSize="small" /> },
];

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ page?: string; userId?: string }>();

  const { isLoading: isLoadingRoles, data: rolesData } = useRoles();
  // FiSci-only pages hang off the server's `can_edit`, never on client flags.
  const { data: realtimeSetting, isLoading: isLoadingCapabilities } = useRealtimeKindsSetting();
  const isFisci = realtimeSetting?.can_edit ?? false;
  const canRunOrganisations = !!user?.can_create_tenants;

  const visiblePages = useMemo(
    () =>
      ADMIN_PAGES.filter((p) => {
        if (p.audience === 'fisci') return isFisci;
        if (p.audience === 'partners') return canRunOrganisations || isFisci;
        return true;
      }),
    [isFisci, canRunOrganisations]
  );

  // /admin/people/:userId has no :page param; the person drawer lives on People.
  const currentKey: AdminPageKey = (params.page as AdminPageKey) || (params.userId ? 'people' : 'overview');
  const current = visiblePages.find((p) => p.key === currentKey);

  // Settings search: a flat index of what people look for, pointing at pages.
  const [query, setQuery] = useState('');
  useEffect(() => setQuery(''), [location.pathname]);
  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [] as SettingsIndexEntry[];
    const allowed = new Set(visiblePages.map((p) => p.key));
    return ADMIN_SETTINGS_INDEX.filter(
      (e) => allowed.has(e.page) && (e.title.toLowerCase().includes(q) || e.keywords.includes(q))
    ).slice(0, 8);
  }, [query, visiblePages]);

  if (!user?.is_staff) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Container>
    );
  }

  // Wait for the audience checks: the nav changes shape once they resolve.
  if (isLoadingRoles || isLoadingCapabilities) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Unknown or not-allowed page: back to the Overview.
  if (!current) return <Navigate to="/admin" replace />;

  const roles = rolesData || [];
  const pageContent: Record<AdminPageKey, React.ReactNode> = {
    overview: <AdminOverview isFisci={isFisci} />,
    people: <UsersManagement roles={roles} />,
    roles: <RolesManagement roles={roles} />,
    'sign-in': <SSOManagement roles={roles} />,
    organisation: <BrandingManagement />,
    organisations: <TenantsManagement />,
    'api-keys': <APIClientsManagement />,
    gis: <GisServicesManagement />,
    'ai-assistants': <MCPConnectionsManagement />,
    'standing-orders': <AgentInstructionsManagement />,
    entitlements: <TenantCapabilitiesManagement />,
  };

  const groups = Array.from(new Set(visiblePages.map((p) => p.group)));

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(var(--vh100) - var(--topbar-h))', bgcolor: '#f4f6f8' }}>
      {/* ── Left navigation ───────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{
          width: 256,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 'var(--topbar-h)',
          alignSelf: 'flex-start',
          height: 'calc(var(--vh100) - var(--topbar-h))',
          overflowY: 'auto',
          py: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ px: 2.5, py: 1, fontWeight: 600 }}>
          Admin
        </Typography>
        <List dense disablePadding>
          {groups.map((group) => (
            <Box key={group ?? 'top'}>
              {group && (
                <ListSubheader
                  disableSticky
                  sx={{ lineHeight: '28px', mt: 1, fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', bgcolor: 'transparent', display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {group}
                  {group === 'FiSci staff only' && (
                    <Chip label="FiSci" size="small" sx={{ height: 16, fontSize: 10, bgcolor: '#7b5cff', color: '#fff' }} />
                  )}
                </ListSubheader>
              )}
              {visiblePages
                .filter((p) => p.group === group)
                .map((p) => (
                  <ListItemButton
                    key={p.key}
                    selected={p.key === currentKey}
                    onClick={() => navigate(p.key === 'overview' ? '/admin' : `/admin/${p.key}`)}
                    sx={{
                      mx: 1,
                      borderRadius: 1.5,
                      '&.Mui-selected': { bgcolor: 'rgba(0, 212, 173, 0.14)', color: '#00705c', fontWeight: 500 },
                      '&.Mui-selected:hover': { bgcolor: 'rgba(0, 212, 173, 0.2)' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, color: 'inherit', opacity: 0.8 }}>{ICONS[p.key]}</ListItemIcon>
                    <ListItemText primary={p.label} />
                  </ListItemButton>
                ))}
            </Box>
          ))}

          <ListSubheader disableSticky sx={{ lineHeight: '28px', mt: 1, fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', bgcolor: 'transparent' }}>
            Data
          </ListSubheader>
          {EXTERNAL_LINKS.map((link) => (
            <ListItemButton key={link.to} onClick={() => navigate(link.to)} sx={{ mx: 1, borderRadius: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 34, opacity: 0.8 }}>{link.icon}</ListItemIcon>
              <ListItemText primary={link.label} />
              <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.5 }} />
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* ── Content ───────────────────────────────────────────────────── */}
      {/* No width cap: tables and card grids fill a large screen; form pages
          arrange themselves in columns instead of stretching one. */}
      <Box sx={{ flex: 1, minWidth: 0, px: { xs: 2, md: 4 }, py: 3, textAlign: 'left' }}>
        <Box sx={{ position: 'relative', mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Find a setting… e.g. “require single sign-on”, “rotate key”, “logo”"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                sx: { bgcolor: 'background.paper', borderRadius: 2 },
              },
            }}
          />
          {query.trim() && (
            <Paper
              elevation={6}
              sx={{ position: 'absolute', top: 44, left: 0, right: 0, zIndex: 10, borderRadius: 2, overflow: 'hidden' }}
            >
              {hits.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                  No settings match.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {hits.map((hit, i) => {
                    const page = ADMIN_PAGES.find((p) => p.key === hit.page)!;
                    return (
                      <Box key={`${hit.page}-${hit.title}`}>
                        {i > 0 && <Divider />}
                        <ListItemButton
                          onClick={() => {
                            setQuery('');
                            navigate(hit.page === 'overview' ? '/admin' : `/admin/${hit.page}`);
                          }}
                        >
                          <ListItemText primary={hit.title} secondary={page.label} />
                        </ListItemButton>
                      </Box>
                    );
                  })}
                </List>
              )}
            </Paper>
          )}
        </Box>

        {pageContent[currentKey]}
      </Box>
    </Box>
  );
}
